/**
 * 소모품 표 — 품명 / 수량 / 비고 세 칸을 보고 고친다.
 *
 * 회사 카드는 수량 칸이 "재고 수량", 현장 카드는 "사용중" 이라서 라벨만 받는다.
 * 좁은 화면(sm 미만)에서는 세 칸짜리 표가 뭉개지므로 줄마다 카드로 바꿔 그린다
 * — SiteTableView 와 같은 방식이다.
 */
import React from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';

import {
  ConsumableDraftRow,
  ConsumableRow,
  newConsumableDraftRow,
  sumConsumableQty,
} from '../services/consumableSheet';

interface ViewProps {
  rows: ConsumableRow[];
  quantityLabel: string;
  emptyText: string;
}

interface EditProps {
  rows: ConsumableDraftRow[];
  quantityLabel: string;
  onChange: (rows: ConsumableDraftRow[]) => void;
  /** 품명 입력칸 추천 목록 — 현장마다 품명이 갈리면 품목별 합계가 쪼개진다. */
  nameOptions?: string[];
  /** datalist 는 문서 전체에서 id 로 찾으므로 카드마다 달라야 한다. */
  nameListId?: string;
}

const QTY_WIDTH = 104;

/** 수량은 숫자만 — 모바일에서 숫자 키패드가 바로 뜨도록 inputMode 를 준다. */
const qtyInputProps = { inputMode: 'numeric' as const, pattern: '[0-9]*', style: { textAlign: 'right' as const } };

export const ConsumableTableView: React.FC<ViewProps> = ({ rows, quantityLabel, emptyText }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    );
  }

  const total = sumConsumableQty(rows);

  if (isMobile) {
    return (
      <Stack divider={<Divider flexItem />} data-testid="consumable-table-cards">
        {rows.map((r, i) => (
          <Box key={`${r.name}-${i}`} sx={{ py: 1 }}>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography variant="body2" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
                {r.name || '—'}
              </Typography>
              <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {r.qty}
              </Typography>
            </Stack>
            {r.note && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ whiteSpace: 'pre-wrap' }}>
                {r.note}
              </Typography>
            )}
          </Box>
        ))}
        <Stack direction="row" justifyContent="space-between" sx={{ pt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {quantityLabel} 합계
          </Typography>
          <Typography variant="caption" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {total}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Table size="small" data-testid="consumable-table">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700 }}>품명</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, width: QTY_WIDTH }}>
            {quantityLabel}
          </TableCell>
          <TableCell sx={{ fontWeight: 700 }}>비고</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={`${r.name}-${i}`} hover>
            <TableCell sx={{ fontWeight: 600 }}>{r.name || '—'}</TableCell>
            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {r.qty}
            </TableCell>
            <TableCell sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>{r.note}</TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell sx={{ color: 'text.secondary', borderBottom: 0 }}>합계</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', borderBottom: 0 }}>
            {total}
          </TableCell>
          <TableCell sx={{ borderBottom: 0 }} />
        </TableRow>
      </TableBody>
    </Table>
  );
};

export const ConsumableTableEditor: React.FC<EditProps> = ({
  rows,
  quantityLabel,
  onChange,
  nameOptions = [],
  nameListId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const patch = (id: string, field: 'name' | 'qty' | 'note', value: string) =>
    onChange(rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  const remove = (id: string) => onChange(rows.filter((r) => r.id !== id));
  const add = () => onChange([...rows, newConsumableDraftRow()]);

  const nameInputProps = nameListId ? { list: nameListId } : undefined;
  const datalist = nameListId && nameOptions.length > 0 && (
    <datalist id={nameListId}>
      {nameOptions.map((n) => (
        <option key={n} value={n} />
      ))}
    </datalist>
  );

  const addButton = (
    <Button
      size="small"
      startIcon={<AddIcon />}
      onClick={add}
      data-testid="consumable-add-row"
      sx={{ textTransform: 'none', alignSelf: 'flex-start', mt: 1 }}
    >
      품목 추가
    </Button>
  );

  if (isMobile) {
    return (
      <Stack>
        {datalist}
        <Stack spacing={1.5}>
          {rows.map((r) => (
            <Paper key={r.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                  <TextField
                    label="품명"
                    size="small"
                    fullWidth
                    value={r.name}
                    onChange={(e) => patch(r.id, 'name', e.target.value)}
                    inputProps={nameInputProps}
                  />
                  <TextField
                    label={quantityLabel}
                    size="small"
                    fullWidth
                    value={r.qty}
                    onChange={(e) => patch(r.id, 'qty', e.target.value)}
                    inputProps={{ ...qtyInputProps, style: undefined }}
                  />
                  <TextField
                    label="비고"
                    size="small"
                    fullWidth
                    value={r.note}
                    onChange={(e) => patch(r.id, 'note', e.target.value)}
                  />
                </Stack>
                <IconButton size="small" onClick={() => remove(r.id)} aria-label={`${r.name || '품목'} 삭제`}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Paper>
          ))}
        </Stack>
        {addButton}
      </Stack>
    );
  }

  return (
    <Stack>
      {datalist}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>품명</TableCell>
            <TableCell sx={{ fontWeight: 700, width: QTY_WIDTH }}>{quantityLabel}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>비고</TableCell>
            <TableCell sx={{ width: 44 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell sx={{ px: 0.5 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="품명"
                  value={r.name}
                  onChange={(e) => patch(r.id, 'name', e.target.value)}
                  inputProps={nameInputProps}
                />
              </TableCell>
              <TableCell sx={{ px: 0.5 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="0"
                  value={r.qty}
                  onChange={(e) => patch(r.id, 'qty', e.target.value)}
                  inputProps={qtyInputProps}
                />
              </TableCell>
              <TableCell sx={{ px: 0.5 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="단위·발주 예정 등"
                  value={r.note}
                  onChange={(e) => patch(r.id, 'note', e.target.value)}
                />
              </TableCell>
              <TableCell sx={{ px: 0 }}>
                <IconButton size="small" onClick={() => remove(r.id)} aria-label={`${r.name || '품목'} 삭제`}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {addButton}
    </Stack>
  );
};
